import { observableValue, type IObservable, type ISettableObservable, type IReader, derived } from '@vscode/observables';
import type { RenderEventType } from './ErrorInfo.js';

export interface ConsoleEvent {
	readonly id: number;
	readonly timestamp: number;
	readonly type: RenderEventType;
	readonly message: string;
	readonly stack?: string;
}

let _nextEventId = 0;

/**
 * Global store for console events (console.error, console.warn, window errors, unhandled rejections).
 * Events are captured globally and cannot be attributed to specific fixtures since they render in parallel.
 */
export class ConsoleEventsStore {
	private static _instance: ConsoleEventsStore | undefined;

	static getInstance(): ConsoleEventsStore {
		if (!this._instance) {
			this._instance = new ConsoleEventsStore();
		}
		return this._instance;
	}

	private readonly _events: ISettableObservable<readonly ConsoleEvent[]>;
	private _installed = false;
	private _originalConsoleError: typeof console.error | undefined;
	private _originalConsoleWarn: typeof console.warn | undefined;

	private constructor() {
		this._events = observableValue<readonly ConsoleEvent[]>(this, []);
	}

	get events(): IObservable<readonly ConsoleEvent[]> {
		return this._events;
	}

	get errorCount(): { read(reader: IReader): number } {
		return derived(this, reader => {
			const events = this._events.read(reader);
			return events.filter((e: ConsoleEvent) => e.type === 'console.error' || e.type === 'window.error' || e.type === 'window.unhandledrejection').length;
		});
	}

	install(): void {
		if (this._installed) return;
		this._installed = true;

		// Intercept console.error
		this._originalConsoleError = console.error;
		console.error = (...args: unknown[]) => {
			this._addEvent('console.error', args.map(String).join(' '));
			this._originalConsoleError!.apply(console, args);
		};

		// Intercept console.warn
		this._originalConsoleWarn = console.warn;
		console.warn = (...args: unknown[]) => {
			this._addEvent('console.warn', args.map(String).join(' '));
			this._originalConsoleWarn!.apply(console, args);
		};

		// Listen for window errors
		window.addEventListener('error', (event) => {
			this._addEvent('window.error', event.message, event.error?.stack);
		});

		// Listen for unhandled promise rejections
		window.addEventListener('unhandledrejection', (event) => {
			const reason = event.reason;
			const message = reason instanceof Error ? reason.message : String(reason);
			const stack = reason instanceof Error ? reason.stack : undefined;
			this._addEvent('window.unhandledrejection', message, stack);
		});
	}

	private _addEvent(type: RenderEventType, message: string, stack?: string): void {
		const event: ConsoleEvent = {
			id: _nextEventId++,
			timestamp: Date.now(),
			type,
			message,
			stack,
		};
		this._events.set([...this._events.get(), event], undefined);
	}

	/**
	 * Add an event programmatically (e.g., forwarded from an iframe).
	 */
	addEvent(type: RenderEventType, message: string, stack?: string): void {
		this._addEvent(type, message, stack);
	}

	clear(): void {
		this._events.set([], undefined);
	}
}
